import type { FC, PropsWithChildren } from 'react';
import React, { createContext, useMemo } from 'react';
import type { FeatureServiceDependencies } from '../FeatureHubAppDefinition';
import type {
  AuthService,
  EnvConfigService,
  EventService,
  TrackingService,
  DealerContextService,
  AudiFootnoteReferenceService,
  AudiHeaderStateService,
  NotificationDisplayService,
  AudiMarketContextService,
  LayerManager,
  GfaServiceConfigProvider,
} from '../feature-service-types';

export interface ServiceContextState {
  readonly authService: AuthService;
  readonly envConfigService: EnvConfigService;
  readonly dealerContextService: DealerContextService;
  readonly footnoteReferenceService: AudiFootnoteReferenceService;
  readonly headerStateService: AudiHeaderStateService;
  readonly notificationDisplayService: NotificationDisplayService;
  readonly layerManager: LayerManager;
  readonly trackingService: TrackingService;
  readonly configProvider: GfaServiceConfigProvider;
  readonly eventService?: EventService;
  readonly marketContextService?: AudiMarketContextService;
}

export const ServiceContext = createContext<ServiceContextState>(
  undefined as unknown as ServiceContextState,
);

export interface ServiceContextProviderProps extends PropsWithChildren {
  readonly featureServices: FeatureServiceDependencies;
}

export const ServiceContextProvider: FC<ServiceContextProviderProps> = ({
  featureServices,
  children,
}) => {
  const authServiceProvider = featureServices['vw:authService'];
  const authService = authServiceProvider.register('myaudi');
  const referenceServiceScopeManager = featureServices['audi-footnote-reference-service'];
  const footnoteReferenceService = referenceServiceScopeManager.getDefaultScopeRefService();

  const state = useMemo<ServiceContextState>(
    () => ({
      authService,
      configProvider: featureServices['gfa:service-config-provider'],
      envConfigService: featureServices['audi:envConfigService'],
      dealerContextService: featureServices['gfa:dealer-context-service'],
      footnoteReferenceService,
      headerStateService: featureServices['audi-header-state-service'],
      notificationDisplayService: featureServices['notification-display-service'],
      layerManager: featureServices['layer-manager'],
      trackingService: featureServices['audi-tracking-service'],
      eventService: featureServices['event-service'],
      marketContextService: featureServices['audi-market-context-service'],
    }),
    [featureServices],
  );

  return <ServiceContext.Provider value={state}>{children}</ServiceContext.Provider>;
};
